import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { fetchAuthSession } from 'aws-amplify/auth';
import Logo from './Logo';
import * as XLSX from 'xlsx';
import { getTenantIdTitleCase } from '../utils/roleUtils';


function CrearUsuario() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('individual'); // 'individual' o 'masivo'
  
  // Mapeo de prefijos de país a nombres de países
  const countryMapping = {
    '+57': 'Colombia',
    '+52': 'Mexico',
    '+56': 'Chile'
  };
  
  // Función helper para obtener el país basado en el prefijo
  const getCountryFromPrefix = (prefix) => {
    return countryMapping[prefix] || 'Mexico'; // Default a Mexico si no se encuentra el prefijo
  };
  
  // Determinar el country_code inicial basado en user_country
  const getInitialCountryCode = () => {
    const userCountry = localStorage.getItem('user_country') || '';
    if (userCountry === 'CHILE') {
      return '+56';
    } else if (userCountry === 'MEXICO') {
      return '+52';
    }
    return '+57'; // Colombia por defecto
  };
  
  // Estados para creación individual
  const [formData, setFormData] = useState({
    user_name: '',
    user_id: '',
    user_phone_prefix: getInitialCountryCode(),
    user_phone_number: '',
    user_email: '',
    user_level: '',
    company_name: '',
    user_brand: '',
    store_name: ''
  });
  
  // Estados para creación masiva
  const [massiveFormData, setMassiveFormData] = useState({
    company_name: '',
    user_brand: '',
    excel_file: null
  });
  
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [isError, setIsError] = useState(false);
  
  // Nuevos estados para manejar resultados detallados
  const [massiveResults, setMassiveResults] = useState(null);
  const [showResultsModal, setShowResultsModal] = useState(false);
  
  // Estados para las opciones dinámicas
  const [companies, setCompanies] = useState([]);
  const [brands, setBrands] = useState([]);
  const [stores, setStores] = useState([]);
  const [loadingOptions, setLoadingOptions] = useState(false);

  // Nuevos estados para el procesamiento avanzado de Excel
  const [excelProcessingStep, setExcelProcessingStep] = useState('upload'); // 'upload', 'sheet-selection', 'column-mapping', 'data-validation'
  const [excelWorkbook, setExcelWorkbook] = useState(null);
  const [excelSheets, setExcelSheets] = useState([]);
  const [selectedSheet, setSelectedSheet] = useState('');
  const [excelHeaders, setExcelHeaders] = useState([]);
  const [excelData, setExcelData] = useState([]);
  const [columnMapping, setColumnMapping] = useState({});
  const [validationData, setValidationData] = useState([]);
  const [countryPrefix, setCountryPrefix] = useState(getInitialCountryCode()); // Default basado en user_country
  
  // Cache para optimizar consultas de API
  const [retailersCache, setRetailersCache] = useState(null);
  const [brandsStoresCache, setBrandsStoresCache] = useState({});

  // Función helper para limpiar y formatear valores
  const cleanAndFormatValue = (value, fieldKey) => {
    if (!value || value === null || value === undefined) {
      return '';
    }
    
    // Aplicar trim inicial a todos los valores
    let cleanValue = value.toString().trim();
    
    // Limpiezas adicionales para el campo de nombre de usuario
    if (fieldKey === 'user_name') {
      // Eliminar símbolos de almohadilla (#)
      cleanValue = cleanValue.replace(/#/g, '');
      
      // Sustituir múltiples espacios seguidos por un único espacio
      cleanValue = cleanValue.replace(/\s{2,}/g, ' ');
      
      // Aplicar trim adicional después de las sustituciones
      cleanValue = cleanValue.trim();
      
      // Convertir a Title Case (mejorado para manejar acentos correctamente)
      cleanValue = cleanValue.toLowerCase().replace(/(^|\s)\S/g, l => l.toUpperCase());
    }
    
    // Convertir a mayúsculas los campos específicos
    if (['company_name', 'user_brand', 'store_name'].includes(fieldKey)) {
      cleanValue = cleanValue.toUpperCase();
    }
    
    return cleanValue;
  };

  // Función helper para obtener el texto dinámico del administrador
  const getAdministratorRoleText = () => {
    const tenantId = localStorage.getItem('tenant_id');
    if (tenantId) {
      const titleCaseTenant = getTenantIdTitleCase(tenantId);
      return `Administrador ${titleCaseTenant}`;
    }
    return 'Administrador Refurbi'; // Fallback por si no hay tenant_id
  };

  // Campos requeridos para el mapeo
  const requiredFields = [
    {
      key: 'user_id',
      label: 'Cédula',
      description: 'Número de identificación personal del usuario',
      icon: '🆔'
    },
    {
      key: 'user_name',
      label: 'Nombre',
      description: 'Nombre completo del usuario',
      icon: '👤'
    },
    {
      key: 'user_email',
      label: 'Correo',
      description: 'Correo electrónico del usuario',
      icon: '📧'
    },
    {
      key: 'user_phone_number',
      label: '# Celular',
      description: 'Número de celular del usuario (sin indicativo de país)',
      icon: '📱'
    },
    {
      key: 'user_level',
      label: 'Rol',
      description: 'Rol del usuario (Promotor, Supervisor de Zona Marca, Coordinador, etc.)',
      icon: '👔'
    },
    {
      key: 'store_name',
      label: 'Tienda',
      description: 'Tienda a la que pertenece el usuario',
      icon: '🏪'
    },
    {
      key: 'user_brand',
      label: 'Marca',
      description: 'Marca a la que pertenece el usuario',
      icon: '🏷️'
    },
    {
      key: 'company_name',
      label: 'Retail',
      description: 'Retailer al que pertenece el usuario',
      icon: '🏢'
    }
  ];

  // Función para obtener headers con autenticación Cognito
  const getAuthHeaders = async () => {
    try {
      const session = await fetchAuthSession();
      const idToken = session.tokens?.idToken?.toString();
      
      if (!idToken) {
        throw new Error('No se pudo obtener el ID token');
      }
      
      return {
        'Authorization': `Bearer ${idToken}`,
        'Content-Type': 'application/json'
      };
    } catch (error) {
      console.error('Error al obtener el token de autenticación:', error);
      throw error;
    }
  };

  // Función para obtener las compañías
  const fetchCompanies = useCallback(async () => {
    setLoadingOptions(true);
    try {
              const headers = await getAuthHeaders();
        const response = await axios.get(
          'https://4svb468mc6.execute-api.us-east-2.amazonaws.com/prod/v1/info',
          {
            headers,
            params: {
              info_type: 'companies',
              tenant_id: localStorage.getItem('tenant_id')
            }
          }
        );

      if (response.status === 200 && response.data.companies) {
        setCompanies(response.data.companies);
      }
    } catch (error) {
      console.error('Error al obtener las compañías:', error);
      setMessage('Error al cargar las compañías. Por favor, recarga la página.');
      setIsError(true);
    } finally {
      setLoadingOptions(false);
    }
  }, []);

  // Cargar las compañías al montar el componente
  useEffect(() => {
    fetchCompanies();
  }, [fetchCompanies]);

  // Función para obtener marcas y tiendas según la compañía seleccionada
  const fetchBrandsAndStores = async (companyName, isForMassive = false) => {
    if (!companyName) {
      setBrands([]);
      if (!isForMassive) {
        setStores([]);
      }
      return;
    }

    setLoadingOptions(true);
    try {
              const headers = await getAuthHeaders();
        const response = await axios.get(
          'https://4svb468mc6.execute-api.us-east-2.amazonaws.com/prod/v1/info',
          {
            headers,
            params: {
              info_type: 'create_user_post_company',
              company_name: companyName,
              tenant_id: localStorage.getItem('tenant_id')
            }
          }
        );

      if (response.status === 200) {
        if (response.data.brands) {
          setBrands(response.data.brands);
        }
        if (response.data.stores && !isForMassive) {
          setStores(response.data.stores); 
        }
      }
    } catch (error) {
      console.error('Error al obtener marcas y tiendas:', error);
      setMessage('Error al cargar las marcas y tiendas. Por favor, intenta seleccionar otra compañía.');
      setIsError(true);
    } finally {
      setLoadingOptions(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Validaciones específicas
    if (name === 'user_id' && !/^[a-zA-Z0-9@._-]*$/.test(value)) {
      return; // Solo permitir letras, números y los caracteres especiales permitidos
    }
    if (name === 'user_phone_number' && !/^\d*$/.test(value)) {
      return; // Solo permitir números
    }
    
    // Si cambia la compañía, cargar marcas y tiendas
    if (name === 'company_name' && value !== formData.company_name) {
      fetchBrandsAndStores(value);
      // Resetear marca y tienda
      setFormData({
        ...formData,
        [name]: value,
        user_brand: '',
        store_name: ''
      });
    } else {
      setFormData({
        ...formData,
        [name]: value
      });
    }
    
    // Limpiar error específico cuando el usuario comienza a corregir
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: ''
      });
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validar que sea un archivo Excel
      const validTypes = [
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      ];
      
      if (!validTypes.includes(file.type) && !file.name.match(/\.(xlsx|xls)$/i)) {
        setMessage('Por favor selecciona un archivo Excel válido (.xlsx o .xls)');
        setIsError(true);
        return;
      }
      
      // Resetear estados del procesamiento anterior
      resetExcelProcessing();
      
      setMassiveFormData({
        ...massiveFormData,
        excel_file: file
      });
      
      // Procesar el archivo Excel
      setIsLoading(true);
      setMessage('Procesando archivo Excel...');
      setIsError(false);
      
      processExcelFile(file)
        .then(() => {
          setMessage('');
          setIsLoading(false);
        })
        .catch(error => {
          setMessage(error.message);
          setIsError(true);
          setIsLoading(false);
          resetExcelProcessing();
        });
      
      // Limpiar mensaje de error si había uno
      if (errors.excel_file) {
        setErrors({
          ...errors,
          excel_file: ''
        });
      }
    }
  };

  // Función para resetear el estado del procesamiento de Excel
  const resetExcelProcessing = () => {
    setExcelProcessingStep('upload');
    setExcelWorkbook(null);
    setExcelSheets([]);
    setSelectedSheet('');
    setExcelHeaders([]);
    setExcelData([]);
    setColumnMapping({});
    setValidationData([]);
    setCountryPrefix(getInitialCountryCode());
  };

  // Función para actualizar datos en la tabla de validación
  const updateValidationData = (rowIndex, fieldKey, newValue) => {
    const updatedData = [...validationData];
    // Limpiar y formatear el nuevo valor
    const cleanValue = cleanAndFormatValue(newValue, fieldKey);
    updatedData[rowIndex].data[fieldKey] = cleanValue;
    
    // Si se cambió el retailer, limpiar marca y tienda si ya no son válidas
    if (fieldKey === 'company_name') {
      const newRetailer = cleanValue;
      const currentBrand = updatedData[rowIndex].data.user_brand;
      const currentStore = updatedData[rowIndex].data.store_name;
      
      if (newRetailer && brandsStoresCache[newRetailer]) {
        const validBrands = brandsStoresCache[newRetailer].brands;
        const validStores = brandsStoresCache[newRetailer].stores;
        
        // Limpiar marca si ya no es válida para el nuevo retailer
        if (currentBrand && !validBrands.includes(currentBrand)) {
          updatedData[rowIndex].data.user_brand = '';
        }
        
        // Limpiar tienda si ya no es válida para el nuevo retailer
        if (currentStore && !validStores.includes(currentStore)) {
          updatedData[rowIndex].data.store_name = '';
        }
      } else if (newRetailer) {
        // Si el retailer cambió pero no tenemos datos en caché, limpiar marca y tienda
        updatedData[rowIndex].data.user_brand = '';
        updatedData[rowIndex].data.store_name = '';
      }
    }
    
    // Revalidar todos los campos de la fila con validación estricta
    const retailers = retailersCache || [];
    requiredFields.forEach(field => {
      const value = updatedData[rowIndex].data[field.key];
      
      // Verificar si el campo está vacío
      if (!value || value === '' || value === null || value === undefined) {
        updatedData[rowIndex].errors[field.key] = 'Este campo es obligatorio';
      } else {
        // Si tiene valor, hacer validación completa
        const error = validateField(field.key, value, updatedData[rowIndex].data, retailers);
        if (error) {
          updatedData[rowIndex].errors[field.key] = error;
        } else {
          delete updatedData[rowIndex].errors[field.key];
        }
      }
    });
    
    setValidationData(updatedData);
  };

  // Función para obtener opciones válidas para un campo
  const getFieldOptions = (fieldKey, rowData) => {
    switch (fieldKey) {
      case 'user_level':
        return [
          'Promotor',
          'Líder Promotoría Marca',
          'Supervisor de Zona Marca',
          'Administrativo de Retail',
          'Administrativo de Marca',
          'Líder Administrativo Marca',
          getAdministratorRoleText()
        ];
        
      case 'company_name':
        return retailersCache || [];
        
      case 'user_brand':
        const retailer = rowData.company_name;
        if (retailer && brandsStoresCache[retailer]) {
          const brands = brandsStoresCache[retailer].brands || [];
          return ['ALL_BRANDS', ...brands];
        }
        return ['ALL_BRANDS'];
        
      case 'store_name':
        const storeRetailer = rowData.company_name;
        if (storeRetailer && brandsStoresCache[storeRetailer]) {
          return brandsStoresCache[storeRetailer].stores || [];
        }
        return [];
        
      default:
        return [];
    }
  };

  // Función para verificar si hay errores en los datos
  const hasValidationErrors = () => {
    return validationData.some(row => Object.keys(row.errors).length > 0);
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (activeTab === 'individual') {
      // Validar campos obligatorios para creación individual
      const requiredFields = [
        'user_name', 'user_id', 'user_email', 'user_level', 
        'company_name', 'user_brand', 'store_name'
      ];
      
      requiredFields.forEach(field => {
        if (!formData[field]) {
          newErrors[field] = 'Este campo es obligatorio';
        }
      });
      
      // Validar formato de email
      if (formData.user_email && !/\S+@\S+\.\S+/.test(formData.user_email)) {
        newErrors.user_email = 'Por favor ingresa un email válido';
      }
      
      // Validar teléfono
      if (!formData.user_phone_number) {
        newErrors.user_phone_number = 'El número de teléfono es obligatorio';
      }
    } else {
      // Validar campos obligatorios para creación masiva
      if (excelProcessingStep === 'data-validation') {
        // En la etapa de validación de datos, no validamos el formulario masivo
        // Los datos vienen del Excel procesado y se validan en hasValidationErrors()
      } else {
        // Solo validar campos de formulario masivo si no estamos en data-validation
        if (!massiveFormData.company_name) {
          newErrors.company_name = 'Debes seleccionar una compañía';
        }
        
        if (!massiveFormData.user_brand) {
          newErrors.user_brand = 'Debes seleccionar una marca';
        }
        
        if (!massiveFormData.excel_file) {
          newErrors.excel_file = 'Debes seleccionar un archivo Excel';
        }
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsLoading(true);
    setMessage('');
    
    try {
      if (activeTab === 'individual') {
        // Creación individual
        const userData = {
          store_id: formData.store_name, // store_id es igual a store_name
          user_id: formData.user_id,
          store_name: formData.store_name,
          company_name: formData.company_name,
          user_brand: formData.user_brand,
          user_name: formData.user_name,
          user_email: formData.user_email,
          country_code: formData.user_phone_prefix,
          user_phone: formData.user_phone_number,
          user_level: formData.user_level,
          user_country: getCountryFromPrefix(formData.user_phone_prefix),
          tenant_id: localStorage.getItem('tenant_id')
        };
        
        const headers = await getAuthHeaders();
        const response = await axios.post(
          'https://4svb468mc6.execute-api.us-east-2.amazonaws.com/prod/v2/users',
          userData,
          { headers }
        );
        
        if (response.status === 200 || response.status === 201) {
          setMessage('¡Usuario creado exitosamente!');
          setIsError(false);
          
          // Limpiar formulario después de éxito
          setFormData({
            user_name: '',
            user_id: '',
            user_phone_prefix: getInitialCountryCode(),
            user_phone_number: '',
            user_email: '',
            user_level: '',
            company_name: '',
            user_brand: '',
            store_name: ''
          });
          
          // Redireccionar después de 2 segundos
          setTimeout(() => {
            navigate('/usuarios');
          }, 2000);
        }
      } else {
        // Creación masiva
        if (excelProcessingStep === 'data-validation') {
          //console.log('🚀 Iniciando creación masiva de usuarios...', validationData.length, 'usuarios');
          // Usar los datos validados para crear usuarios
          const usersToCreate = validationData.map(row => ({
            user_id: row.data.user_id,
            user_name: row.data.user_name,
            user_email: row.data.user_email,
            country_code: countryPrefix,
            user_phone: row.data.user_phone_number,
            user_level: row.data.user_level,
            store_name: row.data.store_name,
            store_id: row.data.store_name, // store_id es igual a store_name
            user_brand: row.data.user_brand,
            company_name: row.data.company_name,
            user_country: getCountryFromPrefix(countryPrefix),
            tenant_id: localStorage.getItem('tenant_id')
          }));
          
          // Estructura optimizada para el endpoint
          const requestData = {
            operation: 'bulk_create_users',
            tenant_id: localStorage.getItem('tenant_id'),
            country_prefix: countryPrefix,
            total_users: usersToCreate.length,
            users: usersToCreate,
            metadata: {
              processed_at: new Date().toISOString(),
              source: 'excel_upload',
              validation_passed: true
            }
          };
          
          const headers = await getAuthHeaders();
          const response = await axios.post(
            'https://4svb468mc6.execute-api.us-east-2.amazonaws.com/prod/v2/users',
            requestData,
            { headers }
          );
          
          if (response.status === 200 || response.status === 201) {
            // Procesar resultados detallados
            let results = [];
            
            // Verificar si la respuesta contiene resultados detallados
            if (response.data && typeof response.data === 'string') {
              try {
                // Intentar parsear si viene como string
                results = JSON.parse(response.data);
              } catch (parseError) {
                // Si no se puede parsear, usar respuesta simple
                results = [];
              }
            } else if (Array.isArray(response.data)) {
              // Si ya viene como array
              results = response.data;
            } else if (response.data && response.data.results && Array.isArray(response.data.results)) {
              // Si viene dentro de un objeto con propiedad results
              results = response.data.results;
            }

            if (results.length > 0) {
              // Mostrar resultados detallados en modal
              setMassiveResults(results);
              setShowResultsModal(true);
              setMessage(''); // Limpiar mensaje general
            } else {
              // Fallback a mensaje simple si no hay resultados detallados
              setMessage(`¡${usersToCreate.length} usuarios creados exitosamente!`);
              setIsError(false);
            }
            
            // Resetear el procesamiento de Excel
            resetExcelProcessing();
            setMassiveFormData({
              company_name: '',
              user_brand: '',
              excel_file: null
            });
            
            // Resetear country prefix al valor inicial
            setCountryPrefix(getInitialCountryCode());
            
            // Limpiar el input de archivo
            const fileInput = document.getElementById('excel_file');
            if (fileInput) {
              fileInput.value = '';
            }
          }
        } else {
          // Solo procesar archivo en etapa de upload (no enviar a servidor aún)
          // El procesamiento ya se hace en handleFileChange
          return;
        }
      }
    } catch (error) {
      console.error('Error al crear usuario(s):', error);
      
      // Manejar código 409 para usuario individual (ya existe)
      if (error.response?.status === 409 && activeTab === 'individual') {
        const errorMessage = error.response?.data?.error || 'Este usuario ya existe en el sistema';
        setMessage(`⚠️ Usuario no creado: ${errorMessage} Por favor verifica la información o contacta al administrador.`);
        setIsError(true);
      } else {
        // Otros errores
        setMessage(error.response?.data?.message || 'Error al crear el/los usuario(s). Por favor, inténtalo de nuevo.');
        setIsError(true);
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Componente para mostrar resultados de creación masiva
  const ResultsModal = ({ results, onClose }) => {
    if (!results) return null;

    const createdUsers = results.filter(user => user.status === 'created');
    const existingUsers = results.filter(user => user.status === 'already_exists' || user.status === 'error');
    const totalUsers = results.length;

    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <h3>📊 Resultados de Creación Masiva</h3>
            <button className="modal-close" onClick={onClose}>×</button>
          </div>
          
          <div className="results-summary">
            <div className="summary-stats">
              <div className="stat-card success">
                <div className="stat-number">{createdUsers.length}</div>
                <div className="stat-label">Usuarios Creados</div>
              </div>
              <div className="stat-card warning">
                <div className="stat-number">{existingUsers.length}</div>
                <div className="stat-label">Ya Existían</div>
              </div>
              <div className="stat-card info">
                <div className="stat-number">{totalUsers}</div>
                <div className="stat-label">Total Procesados</div>
              </div>
            </div>
          </div>

          <div className="results-details">
            {createdUsers.length > 0 && (
              <div className="results-section">
                <h4 className="section-title success">✅ Usuarios Creados Exitosamente ({createdUsers.length})</h4>
                <div className="results-list">
                  {createdUsers.map((user, index) => (
                    <div key={index} className="result-item success">
                      <span className="user-id">{user.user_id}</span>
                      <span className="status-badge success">Creado</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {existingUsers.length > 0 && (
              <div className="results-section">
                <h4 className="section-title warning">⚠️ Usuarios que Ya Existían ({existingUsers.length})</h4>
                <div className="results-list">
                  {existingUsers.map((user, index) => (
                    <div key={index} className="result-item warning">
                      <span className="user-id">{user.user_id}</span>
                      <span className="status-badge warning">Ya existe</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="modal-actions">
            <button className="btn btn-primary" onClick={onClose}>
              Entendido
            </button>
            <button className="btn btn-outline" onClick={() => navigate('/usuarios')}>
              Ir a Lista de Usuarios
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Función para procesar archivo Excel
  const processExcelFile = async (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: 'array' });
          
          // Validar número de hojas
          const sheetNames = workbook.SheetNames;
          if (sheetNames.length > 10) {
            reject(new Error('El archivo Excel no puede tener más de 10 hojas. Por favor, reduce el número de hojas.'));
            return;
          }
          
          setExcelWorkbook(workbook);
          setExcelSheets(sheetNames);
          
          if (sheetNames.length === 1) {
            // Si solo hay una hoja, procesarla automáticamente
            setSelectedSheet(sheetNames[0]);
            processSelectedSheet(workbook, sheetNames[0]);
            resolve();
          } else {
            // Si hay múltiples hojas, permitir al usuario seleccionar
            setExcelProcessingStep('sheet-selection');
            resolve();
          }
        } catch (error) {
          reject(new Error('Error al leer el archivo Excel. Asegúrate de que sea un archivo Excel válido.'));
        }
      };
      
      reader.onerror = () => {
        reject(new Error('Error al leer el archivo.'));
      };
      
      reader.readAsArrayBuffer(file);
    });
  };

  // Función para procesar la hoja seleccionada
  const processSelectedSheet = (workbook, sheetName) => {
    try {
      const worksheet = workbook.Sheets[sheetName];
      const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
      
      if (jsonData.length === 0) {
        throw new Error('La hoja seleccionada está vacía.');
      }
      
      // Obtener headers (primera fila)
      const headers = jsonData[0].filter(header => header && header.toString().trim() !== '');
      
      if (headers.length === 0) {
        throw new Error('No se encontraron columnas válidas en la primera fila.');
      }
      
      // Obtener datos (filas restantes)
      const dataRows = jsonData.slice(1).filter(row => 
        row.some(cell => cell && cell.toString().trim() !== '')
      );
      
      if (dataRows.length === 0) {
        throw new Error('No se encontraron datos en la hoja seleccionada.');
      }
      
      setExcelHeaders(headers);
      setExcelData(dataRows);
      
      // Mapeo automático de columnas
      const autoMapping = performAutoMapping(headers);
      setColumnMapping(autoMapping);
      setExcelProcessingStep('column-mapping');
      
    } catch (error) {
      setMessage(`Error al procesar la hoja: ${error.message}`);
      setIsError(true);
    }
  };

  // Función para mapeo automático de columnas
  const performAutoMapping = (headers) => {
    const mapping = {};
    
    // Patrones de coincidencia para cada campo
    const patterns = {
      user_id: ['cedula', 'cédula', 'id', 'identificacion', 'identificación', 'documento', 'dni'],
      user_name: ['nombre', 'name', 'nombres', 'usuario', 'user'],
      user_email: ['email', 'correo', 'mail', 'e-mail', 'electronico'],
      user_phone_number: ['telefono', 'teléfono', 'celular', 'movil', 'móvil', 'phone', 'cel'],
      user_level: ['rol', 'role', 'cargo', 'nivel', 'position', 'puesto'],
      store_name: ['tienda', 'store', 'sucursal', 'punto'],
      user_brand: ['marca', 'brand'],
      company_name: ['retail', 'retailer', 'empresa', 'company', 'compañia', 'compañía']
    };
    
    headers.forEach((header, index) => {
      const headerLower = header.toString().toLowerCase().trim();
      
      for (const [fieldKey, patternList] of Object.entries(patterns)) {
        if (patternList.some(pattern => headerLower.includes(pattern))) {
          if (!mapping[fieldKey]) { // Solo mapear si no está ya mapeado
            mapping[fieldKey] = index;
          }
        }
      }
    });
    
    return mapping;
  };

  // Función para manejar la selección de hoja
  const handleSheetSelection = (sheetName) => {
    setSelectedSheet(sheetName);
    processSelectedSheet(excelWorkbook, sheetName);
  };

  // Función para actualizar mapeo de columnas
  const updateColumnMapping = (fieldKey, columnIndex) => {
    const newMapping = { ...columnMapping };
    
    // Remover mapeo previo de esta columna
    Object.keys(newMapping).forEach(key => {
      if (newMapping[key] === columnIndex) {
        delete newMapping[key];
      }
    });
    
    // Agregar nuevo mapeo
    if (columnIndex !== null && columnIndex !== undefined && columnIndex !== '') {
      newMapping[fieldKey] = parseInt(columnIndex);
    } else {
      delete newMapping[fieldKey];
    }
    
    setColumnMapping(newMapping);
  };

  // Verificar si el mapeo está completo
  const isMappingComplete = () => {
    return requiredFields.every(field => columnMapping[field.key] !== undefined);
  };

  // Función para obtener retailers (solo una vez)
  const fetchRetailers = async () => {
    if (retailersCache) {
      return retailersCache;
    }
    
    try {
      const headers = await getAuthHeaders();
      const response = await axios.get(
        'https://4svb468mc6.execute-api.us-east-2.amazonaws.com/prod/v1/info',
        {
          headers,
          params: {
            info_type: 'companies',
            tenant_id: localStorage.getItem('tenant_id')
          }
        }
      );

      if (response.status === 200 && response.data.companies) {
        setRetailersCache(response.data.companies);
        return response.data.companies;
      }
      return [];
    } catch (error) {
      console.error('Error al obtener retailers:', error);
      return [];
    }
  };

  // Función para obtener marcas y tiendas de un retailer específico
  const fetchBrandsAndStoresForRetailer = async (retailerName) => {
    if (brandsStoresCache[retailerName]) {
      return brandsStoresCache[retailerName];
    }
    
    try {
      const headers = await getAuthHeaders();
      const response = await axios.get(
        'https://4svb468mc6.execute-api.us-east-2.amazonaws.com/prod/v1/info',
        {
          headers,
          params: {
            info_type: 'create_user_post_company',
            company_name: retailerName,
            tenant_id: localStorage.getItem('tenant_id')
          }
        }
      );

      if (response.status === 200) {
        const data = {
          brands: response.data.brands || [],
          stores: response.data.stores || []
        };
        
        setBrandsStoresCache(prev => ({
          ...prev,
          [retailerName]: data
        }));
        
        return data;
      }
      return { brands: [], stores: [] };
    } catch (error) {
      console.error(`Error al obtener marcas y tiendas para ${retailerName}:`, error);
      return { brands: [], stores: [] };
    }
  };

  // Función para validar los datos y prepararlos para la tabla
  const validateAndPrepareData = async () => {
    setIsLoading(true);
    setMessage('Obteniendo información de retailers...');
    setIsError(false);
    
    try {
      // Obtener retailers
      const retailers = await fetchRetailers();
      
      // Preparar datos para validación
      const validatedData = [];
      const uniqueRetailers = new Set();
      
      // Recopilar retailers únicos primero
      excelData.forEach(row => {
        const retailerIndex = columnMapping.company_name;
        if (retailerIndex !== undefined && row[retailerIndex]) {
          const cleanRetailer = cleanAndFormatValue(row[retailerIndex], 'company_name');
          if (cleanRetailer) {
            uniqueRetailers.add(cleanRetailer);
          }
        }
      });
      
      setMessage(`Cargando marcas y tiendas para ${uniqueRetailers.size} retailers...`);
      
      // Obtener marcas y tiendas para cada retailer único y construir caché local
      const localBrandsStoresCache = {};
      for (const retailer of uniqueRetailers) {
        const data = await fetchBrandsAndStoresForRetailer(retailer);
        localBrandsStoresCache[retailer] = data;
      }
      
      // Actualizar el estado del caché
      setBrandsStoresCache(prev => ({
        ...prev,
        ...localBrandsStoresCache
      }));
      
      setMessage('Validando datos de usuarios...');
      
      // Validar cada fila de datos
      for (let i = 0; i < excelData.length; i++) {
        const row = excelData[i];
        const validatedRow = {
          id: i,
          originalRow: row,
          data: {},
          errors: {}
        };
        
        // Procesar cada campo mapeado
        requiredFields.forEach(field => {
          const columnIndex = columnMapping[field.key];
          if (columnIndex !== undefined) {
            const rawValue = row[columnIndex];
            const cleanValue = cleanAndFormatValue(rawValue, field.key);
            validatedRow.data[field.key] = cleanValue;
          }
        });
        
        validatedData.push(validatedRow);
      }
      
      // Primero, limpiar campos dependientes inválidos que vienen del Excel
      validatedData.forEach((validatedRow) => {
        const retailer = validatedRow.data.company_name;
        if (retailer && retailers.includes(retailer) && localBrandsStoresCache[retailer]) {
            const validBrands = localBrandsStoresCache[retailer].brands || [];
            const validStores = localBrandsStoresCache[retailer].stores || [];
            
            // Si la marca del Excel no es válida para este retailer, la limpiamos.
            if (validatedRow.data.user_brand && !validBrands.includes(validatedRow.data.user_brand)) {
                validatedRow.data.user_brand = '';
            }
            
            // Si la tienda del Excel no es válida para este retailer, la limpiamos.
            if (validatedRow.data.store_name && !validStores.includes(validatedRow.data.store_name)) {
                validatedRow.data.store_name = '';
            }
        }
      });
      
      // Función de validación que usa el caché local
      const validateFieldWithLocalCache = (fieldKey, value, rowData, retailers, localCache) => {
        // Primera validación genérica: si está vacío
        if (!value || value === '' || value === null || value === undefined) {
          return 'Este campo es obligatorio';
        }
        
        switch (fieldKey) {
          case 'user_email':
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailRegex.test(value)) {
              return 'Formato de email inválido';
            }
            break;
            
          case 'user_phone_number':
            const phoneRegex = /^\d{7,15}$/;
            if (!phoneRegex.test(value)) {
              return 'Número de teléfono inválido (solo números, 7-15 dígitos)';
            }
            break;
            
          case 'user_level':
            const validRoles = [
              'Promotor',
              'Líder Promotoría Marca',
              'Supervisor de Zona Marca',
              'Administrativo de Retail',
              'Administrativo de Marca',
              'Líder Administrativo Marca',
              getAdministratorRoleText()
            ];
            if (!validRoles.includes(value)) {
              return 'Rol inválido';
            }
            break;
            
          case 'company_name':
            if (!retailers.includes(value)) {
              return 'Retailer inválido';
            }
            break;
            
          case 'user_brand': {
            const retailer = rowData.company_name;
            // Validar primero el retailer
            if (!retailer || !retailers.includes(retailer)) {
              return 'Primero selecciona un retailer válido';
            }
            // Permitir "ALL_BRANDS" como opción válida
            if (value === 'ALL_BRANDS') {
              break;
            }
            // Usar el caché local
            const cache = localCache[retailer];
            const validBrands = cache ? (cache.brands || []) : [];
            if (!validBrands.includes(value)) {
              return 'Marca inválida para este retailer';
            }
            break;
          }
            
          case 'store_name': {
            const retailer = rowData.company_name;
            // Validar primero el retailer
            if (!retailer || !retailers.includes(retailer)) {
              return 'Primero selecciona un retailer válido';
            }
            // Usar el caché local
            const cache = localCache[retailer];
            const validStores = cache ? (cache.stores || []) : [];
            if (!validStores.includes(value)) {
              return 'Tienda inválida para este retailer';
            }
            break;
          }
          
          default:
            // No hay validación específica para otros campos
            break;
        }
        
        return null;
      };

      // Validación final específica para campos que muestran "Seleccionar..."
      const performFinalValidation = () => {
        validatedData.forEach((validatedRow) => {
          requiredFields.forEach(field => {
            const value = validatedRow.data[field.key];
            
            // Verificar si el campo está vacío
            if (!value || value === '' || value === null || value === undefined) {
              validatedRow.errors[field.key] = 'Este campo es obligatorio';
            } else {
              // Si tiene valor, hacer validación completa usando el caché local
              const error = validateFieldWithLocalCache(field.key, value, validatedRow.data, retailers, localBrandsStoresCache);
              if (error) {
                validatedRow.errors[field.key] = error;
              } else {
                delete validatedRow.errors[field.key];
              }
            }
          });
        });
      };
      
      // Ejecutar validación final
      performFinalValidation();
      
      setValidationData(validatedData);
      setExcelProcessingStep('data-validation');
      setMessage('');
      
    } catch (error) {
      setMessage(`Error durante la validación: ${error.message}`);
      setIsError(true);
    } finally {
      setIsLoading(false);
    }
  };

  // Función para validar un campo específico
  const validateField = (fieldKey, value, rowData, retailers) => {
    // Primera validación genérica: si está vacío
    if (!value || value === '' || value === null || value === undefined) {
      return 'Este campo es obligatorio';
    }
    
    switch (fieldKey) {
      case 'user_email':
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(value)) {
          return 'Formato de email inválido';
        }
        break;
        
      case 'user_phone_number':
        const phoneRegex = /^\d{7,15}$/;
        if (!phoneRegex.test(value)) {
          return 'Número de teléfono inválido (solo números, 7-15 dígitos)';
        }
        break;
        
      case 'user_level':
        const validRoles = [
          'Promotor',
          'Líder Promotoría Marca',
          'Supervisor de Zona Marca',
          'Administrativo de Retail',
          'Administrativo de Marca',
          'Líder Administrativo Marca',
          getAdministratorRoleText()
        ];
        if (!validRoles.includes(value)) {
          return 'Rol inválido';
        }
        break;
        
      case 'company_name':
        if (!retailers.includes(value)) {
          return 'Retailer inválido';
        }
        break;
        
      case 'user_brand': {
        const retailer = rowData.company_name;
        // Validar primero el retailer
        if (!retailer || !retailers.includes(retailer)) {
          return 'Primero selecciona un retailer válido';
        }
        // Permitir "ALL_BRANDS" como opción válida
        if (value === 'ALL_BRANDS') {
          break;
        }
        // Siempre validar contra la lista de marcas, aunque esté vacía
        const cache = brandsStoresCache[retailer];
        const validBrands = cache ? (cache.brands || []) : [];
        if (!validBrands.includes(value)) {
          return 'Marca inválida para este retailer';
        }
        break;
      }
        
      case 'store_name': {
        const retailer = rowData.company_name;
        // Validar primero el retailer
        if (!retailer || !retailers.includes(retailer)) {
          return 'Primero selecciona un retailer válido';
        }
        // Siempre validar contra la lista de tiendas, aunque esté vacía
        const cache = brandsStoresCache[retailer];
        const validStores = cache ? (cache.stores || []) : [];
        if (!validStores.includes(value)) {
          return 'Tienda inválida para este retailer';
        }
        break;
      }
      
      default:
        // No hay validación específica para otros campos
        break;
    }
    
    return null;
  };

  return (
    <div className="crear-usuario-container">
      <header className="header">
        <Logo 
          variant="horizontal" 
          size="medium" 
          onClick={() => navigate('/dashboard')} 
        />
        <div className="header-title">Crear Usuario</div>
        <div className="user-menu">
          <button className="btn btn-outline" onClick={() => navigate('/usuarios')}>Volver a Usuarios</button>
        </div>
      </header>
      
      <div className="container">
        <div className="card animate-fade-in">
          <h2 className="card-title">Crear Usuario</h2>
          <p className="card-description">
            {activeTab === 'individual' 
              ? 'Completa el formulario para registrar un nuevo usuario en el sistema.'
              : 'Sube un archivo Excel para crear múltiples usuarios de forma masiva.'
            }
          </p>
          
          {/* Pestañas */}
          <div className="tabs-container">
            <div className="tabs">
              <button 
                className={`tab ${activeTab === 'individual' ? 'tab-active' : ''}`}
                onClick={() => {
                  setActiveTab('individual');
                  setMessage('');
                  setErrors({});
                  setMassiveResults(null);
                  setShowResultsModal(false);
                  // Resetear country code al cambiar de tab
                  setFormData(prev => ({ ...prev, user_phone_prefix: getInitialCountryCode() }));
                }}
              >
                <span className="tab-icon">👤</span>
                Crear Usuario Individual
              </button>
              <button 
                className={`tab ${activeTab === 'masivo' ? 'tab-active' : ''}`}
                onClick={() => {
                  setActiveTab('masivo');
                  setMessage('');
                  setErrors({});
                  setMassiveResults(null);
                  setShowResultsModal(false);
                  // Resetear country code al cambiar de tab
                  setCountryPrefix(getInitialCountryCode());
                }}
              >
                <span className="tab-icon">📊</span>
                Crear Usuarios Masivamente
              </button>
            </div>
          </div>
          
          {message && (
            <div className={`message ${isError ? 'message-error' : 'message-success'}`}>
              {message}
            </div>
          )}
          
          {loadingOptions && (
            <div className="loading-options">
              <div className="loading-spinner-small"></div>
              <span>Cargando opciones...</span>
            </div>
          )}
          
          <form onSubmit={handleSubmit} className="create-user-form">
            {activeTab === 'individual' ? (
              <>
                {/* Formulario de creación individual */}
                {/* Nombre completo y ID */}
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="user_name" className="form-label">Nombre Completo</label>
                    <input
                      id="user_name"
                      name="user_name"
                      type="text"
                      className={`form-input ${errors.user_name ? 'form-input-error' : ''}`}
                      placeholder="Ingresa el nombre completo"
                      value={formData.user_name}
                      onChange={handleChange}
                    />
                    {errors.user_name && <div className="error-message">{errors.user_name}</div>}
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="user_id" className="form-label">Identificación Personal o id de usuario</label>
                    <input
                      id="user_id"
                      name="user_id"
                      type="text"
                      className={`form-input ${errors.user_id ? 'form-input-error' : ''}`}
                      placeholder="Ingresa el número de identificación"
                      value={formData.user_id}
                      onChange={handleChange}
                    />
                    {errors.user_id && <div className="error-message">{errors.user_id}</div>}
                    <div className="input-help-text">Solo letras, números, guion medio (-), guion bajo (_), arroba (@) y punto (.)</div>
                  </div>
                </div>
                
                {/* Teléfono y Email */}
                <div className="form-row">
                  <div className="form-group phone-group">
                    <label htmlFor="user_phone_number" className="form-label">Teléfono</label>
                    <div className="phone-input-container">
                      <select
                        id="user_phone_prefix"
                        name="user_phone_prefix"
                        className="form-select phone-prefix"
                        value={formData.user_phone_prefix}
                        onChange={handleChange}
                      >
                        {Object.entries(countryMapping).map(([prefix, country]) => (
                          <option key={prefix} value={prefix}>{prefix} ({country})</option>
                        ))}
                      </select>
                      <input
                        id="user_phone_number"
                        name="user_phone_number"
                        type="text"
                        className={`form-input ${errors.user_phone_number ? 'form-input-error' : ''}`}
                        placeholder="Número de teléfono"
                        value={formData.user_phone_number}
                        onChange={handleChange}
                      />
                    </div>
                    {errors.user_phone_number && <div className="error-message">{errors.user_phone_number}</div>}
                    <div className="input-help-text">Solo números permitidos</div>
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="user_email" className="form-label">Correo Electrónico</label>
                    <input
                      id="user_email"
                      name="user_email"
                      type="email"
                      className={`form-input ${errors.user_email ? 'form-input-error' : ''}`}
                      placeholder="Ingresa el correo electrónico"
                      value={formData.user_email}
                      onChange={handleChange}
                    />
                    {errors.user_email && <div className="error-message">{errors.user_email}</div>}
                  </div>
                </div>
                
                {/* Nivel de usuario y Compañía */}
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="user_level" className="form-label">Nivel de Usuario</label>
                    <select
                      id="user_level"
                      name="user_level"
                      className={`form-select ${errors.user_level ? 'form-input-error' : ''}`}
                      value={formData.user_level}
                      onChange={handleChange}
                    >
                      <option value="">Seleccionar nivel...</option>
                      <option value="Promotor">Promotor</option>
                      <option value="Líder Promotoría Marca">Líder Promotoría Marca</option>
                      <option value="Supervisor de Zona Marca">Supervisor de Zona Marca</option>
                      <option value="Administrativo de Retail">Administrativo de Retail</option>
                      <option value="Administrativo de Marca">Administrativo de Marca</option>
                      <option value="Líder Administrativo Marca">Líder Administrativo Marca</option>
                      <option value={getAdministratorRoleText()}>{getAdministratorRoleText()}</option>
                    </select>
                    {errors.user_level && <div className="error-message">{errors.user_level}</div>}
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="company_name" className="form-label">Nombre de la Compañía</label>
                    <select
                      id="company_name"
                      name="company_name"
                      className={`form-select ${errors.company_name ? 'form-input-error' : ''}`}
                      value={formData.company_name}
                      onChange={handleChange}
                      disabled={companies.length === 0}
                    >
                      <option value="">Seleccionar compañía...</option>
                      {companies.map((company, index) => (
                        <option key={index} value={company}>{company}</option>
                      ))}
                    </select>
                    {errors.company_name && <div className="error-message">{errors.company_name}</div>}
                  </div>
                </div>
                
                {/* Marca y Tienda */}
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="user_brand" className="form-label">Marca</label>
                    <select
                      id="user_brand"
                      name="user_brand"
                      className={`form-select ${errors.user_brand ? 'form-input-error' : ''}`}
                      value={formData.user_brand}
                      onChange={handleChange}
                      disabled={brands.length === 0 || !formData.company_name}
                    >
                      <option value="">Seleccionar marca...</option>
                      <option value="ALL_BRANDS">Todas las marcas</option>
                      {brands.map((brand, index) => (
                        <option key={index} value={brand}>{brand}</option>
                      ))}
                    </select>
                    {errors.user_brand && <div className="error-message">{errors.user_brand}</div>}
                    {!formData.company_name && <div className="input-help-text">Primero selecciona una compañía</div>}
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="store_name" className="form-label">Tienda asignada</label>
                    <select
                      id="store_name"
                      name="store_name"
                      className={`form-select ${errors.store_name ? 'form-input-error' : ''}`}
                      value={formData.store_name}
                      onChange={handleChange}
                      disabled={stores.length === 0 || !formData.company_name}
                    >
                      <option value="">Seleccionar tienda...</option>
                      {stores.map((store, index) => (
                        <option key={index} value={store}>{store}</option>
                      ))}
                    </select>
                    {errors.store_name && <div className="error-message">{errors.store_name}</div>}
                    {!formData.company_name && <div className="input-help-text">Primero selecciona una compañía</div>}
                  </div>
                </div>
              </>
            ) : (
              <>
                {/* Formulario de creación masiva avanzado */}
                {excelProcessingStep === 'upload' && (
                  <>
                    <div className="massive-creation-info">
                      <div className="info-card">
                        <h3>📋 Procesamiento Inteligente de Excel</h3>
                        <ul>
                          <li>📁 Sube tu archivo Excel y lo procesaremos automáticamente</li>
                          <li>🔍 Detectaremos y mapearemos las columnas automáticamente</li>
                          <li>✅ Validaremos todos los datos antes de la creación</li>
                          <li>📊 Máximo 10 hojas por archivo</li>
                          <li>🎯 Formatos soportados: .xlsx, .xls</li>
                        </ul>
                      </div>
                    </div>
                    
                    <div className="form-group">
                      <label htmlFor="excel_file" className="form-label">Archivo Excel</label>
                      <div className="file-input-container">
                        <input
                          id="excel_file"
                          name="excel_file"
                          type="file"
                          accept=".xlsx,.xls"
                          className={`form-input file-input ${errors.excel_file ? 'form-input-error' : ''}`}
                          onChange={handleFileChange}
                        />
                        <div className="file-input-label">
                          <span className="file-icon">📁</span>
                          {massiveFormData.excel_file ? massiveFormData.excel_file.name : 'Seleccionar archivo Excel...'}
                        </div>
                      </div>
                      {errors.excel_file && <div className="error-message">{errors.excel_file}</div>}
                      <div className="input-help-text">Selecciona un archivo Excel (.xlsx o .xls) con los datos de los usuarios</div>
                    </div>
                  </>
                )}

                {excelProcessingStep === 'sheet-selection' && (
                  <>
                    <div className="excel-step-header">
                      <h3>📋 Seleccionar Hoja de Excel</h3>
                      <p>Tu archivo tiene múltiples hojas. Selecciona la hoja que contiene los datos de usuarios:</p>
                    </div>
                    
                    <div className="sheet-selection-container">
                      {excelSheets.map((sheetName, index) => (
                        <button
                          key={index}
                          type="button"
                          className={`sheet-option ${selectedSheet === sheetName ? 'selected' : ''}`}
                          onClick={() => handleSheetSelection(sheetName)}
                        >
                          <span className="sheet-icon">📄</span>
                          <span className="sheet-name">{sheetName}</span>
                        </button>
                      ))}
                    </div>
                    
                    <div className="step-actions">
                      <button 
                        type="button" 
                        className="btn btn-outline" 
                        onClick={() => {
                          resetExcelProcessing();
                          setMassiveFormData({ ...massiveFormData, excel_file: null });
                          const fileInput = document.getElementById('excel_file');
                          if (fileInput) fileInput.value = '';
                        }}
                      >
                        ← Cambiar Archivo
                      </button>
                    </div>
                  </>
                )}

                {excelProcessingStep === 'column-mapping' && (
                  <>
                    <div className="excel-step-header">
                      <h3>🎯 Mapear Columnas</h3>
                      <p>Asocia las columnas de tu Excel con nuestros campos requeridos. <strong>Hemos detectado automáticamente algunas coincidencias.</strong></p>
                    </div>
                    
                    <div className="column-mapping-container">
                      <div className="mapping-section">
                        <h4>📊 Columnas de tu Excel:</h4>
                        <div className="excel-columns">
                          {excelHeaders.map((header, index) => (
                            <div key={index} className="excel-column">
                              <span className="column-index">{index + 1}</span>
                              <span className="column-name">{header}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                      
                      <div className="mapping-section">
                        <h4>🎯 Campos Requeridos:</h4>
                        <div className="required-fields">
                          {requiredFields.map((field) => (
                            <div key={field.key} className="field-mapping">
                              <div className="field-info">
                                <span className="field-icon">{field.icon}</span>
                                <span className="field-label">{field.label}</span>
                                <div className="field-tooltip">
                                  <span className="tooltip-icon">ℹ️</span>
                                  <div className="tooltip-content">
                                    {field.description}
                                  </div>
                                </div>
                              </div>
                              
                              <select
                                className={`mapping-select ${columnMapping[field.key] !== undefined && columnMapping[field.key] !== '' ? 'mapped' : ''}`}
                                value={columnMapping[field.key] !== undefined ? columnMapping[field.key] : ''}
                                onChange={(e) => updateColumnMapping(field.key, e.target.value)}
                              >
                                <option value="">Sin asignar</option>
                                {excelHeaders.map((header, index) => (
                                  <option key={index} value={index}>
                                    {index + 1}. {header}
                                  </option>
                                ))}
                              </select>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                    
                    <div className="mapping-progress">
                      <div className="progress-info">
                        <span>Progreso del mapeo: {Object.keys(columnMapping).length} de {requiredFields.length} campos</span>
                        <div className="progress-bar">
                          <div 
                            className="progress-fill" 
                            style={{ width: `${(Object.keys(columnMapping).length / requiredFields.length) * 100}%` }}
                          />
                        </div>
                      </div>
                    </div>
                    
                    <div className="step-actions">
                      <button 
                        type="button" 
                        className="btn btn-outline" 
                        onClick={() => setExcelProcessingStep('sheet-selection')}
                      >
                        ← Cambiar Hoja
                      </button>
                      <button 
                        type="button" 
                        className={`btn btn-primary ${!isMappingComplete() ? 'disabled' : ''}`}
                        disabled={!isMappingComplete()}
                        onClick={validateAndPrepareData}
                      >
                        Continuar → Validar Datos
                      </button>
                    </div>
                  </>
                )}

                {excelProcessingStep === 'data-validation' && (
                  <>
                    <div className="excel-step-header">
                      <h3>✅ Validar y Editar Datos</h3>
                      <p>Revisa y edita los datos antes de crear los usuarios. Los campos con errores aparecen en rojo.</p>
                    </div>
                    
                    <div className="country-selector">
                      <label className="form-label">Indicativo de País para Teléfonos:</label>
                      <select 
                        className="form-select"
                        value={countryPrefix}
                        onChange={(e) => setCountryPrefix(e.target.value)}
                      >
                        {Object.entries(countryMapping).map(([prefix, country]) => (
                          <option key={prefix} value={prefix}>{prefix} ({country})</option>
                        ))}
                      </select>
                    </div>
                    
                    <div className="validation-summary">
                      <div className="summary-stats">
                        <div className="stat">
                          <span className="stat-number">{validationData.length}</span>
                          <span className="stat-label">Total Usuarios</span>
                        </div>
                        <div className="stat error">
                          <span className="stat-number">{validationData.filter(row => Object.keys(row.errors).length > 0).length}</span>
                          <span className="stat-label">Con Errores</span>
                        </div>
                        <div className="stat success">
                          <span className="stat-number">{validationData.filter(row => Object.keys(row.errors).length === 0).length}</span>
                          <span className="stat-label">Válidos</span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="validation-table-container">
                      <table className="validation-table">
                        <thead>
                          <tr>
                            <th>#</th>
                            {requiredFields.map(field => (
                              <th key={field.key}>
                                {field.icon} {field.label}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {validationData.map((row, rowIndex) => (
                            <tr key={rowIndex} className={Object.keys(row.errors).length > 0 ? 'has-errors' : ''}>
                              <td className="row-number">{rowIndex + 1}</td>
                              {requiredFields.map(field => (
                                <td key={field.key} className={row.errors[field.key] ? 'error-cell' : ''}>
                                  {['user_level', 'company_name', 'user_brand', 'store_name'].includes(field.key) ? (
                                    <select
                                      className={`validation-select ${row.errors[field.key] ? 'error' : ''}`}
                                      value={row.data[field.key] || ''}
                                      onChange={(e) => updateValidationData(rowIndex, field.key, e.target.value)}
                                    >
                                      <option value="">Seleccionar...</option>
                                      {getFieldOptions(field.key, row.data).map((option, optIndex) => (
                                        <option key={optIndex} value={option}>
                                          {option === 'ALL_BRANDS' ? 'Todas las marcas' : option}
                                        </option>
                                      ))}
                                    </select>
                                  ) : field.key === 'user_phone_number' ? (
                                    <div className="phone-validation">
                                      <span className="phone-prefix">{countryPrefix}</span>
                                      <input
                                        type="text"
                                        className={`validation-input ${row.errors[field.key] ? 'error' : ''}`}
                                        value={row.data[field.key] || ''}
                                        onChange={(e) => updateValidationData(rowIndex, field.key, e.target.value)}
                                        placeholder="Número..."
                                      />
                                    </div>
                                  ) : (
                                    <input
                                      type={field.key === 'user_email' ? 'email' : 'text'}
                                      className={`validation-input ${row.errors[field.key] ? 'error' : ''}`}
                                      value={row.data[field.key] || ''}
                                      onChange={(e) => updateValidationData(rowIndex, field.key, e.target.value)}
                                      placeholder={field.label}
                                    />
                                  )}
                                  {row.errors[field.key] && (
                                    <div className="cell-error">{row.errors[field.key]}</div>
                                  )}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    
                    <div className="step-actions">
                      <button 
                        type="button" 
                        className="btn btn-outline" 
                        onClick={() => setExcelProcessingStep('column-mapping')}
                      >
                        ← Cambiar Mapeo
                      </button>
                      <button 
                        type="submit" 
                        className={`btn btn-primary ${hasValidationErrors() ? 'disabled' : ''}`}
                        disabled={hasValidationErrors() || isLoading}
                      >
                        {isLoading ? 'Creando Usuarios...' : `✨ Crear ${validationData.length} Usuarios`}
                      </button>
                    </div>
                  </>
                )}
              </>
            )}
            
            <div className="form-actions">
              {(activeTab === 'individual' || (activeTab === 'masivo' && excelProcessingStep === 'upload')) && (
                <>
                  <button 
                    type="submit" 
                    className={`btn btn-primary ${isLoading ? 'btn-loading' : ''}`}
                    disabled={isLoading || loadingOptions}
                  >
                    {isLoading ? (
                      activeTab === 'individual' ? 'Creando usuario...' : 'Procesando archivo...'
                    ) : (
                      activeTab === 'individual' ? 'Crear Usuario' : 'Procesar Archivo Excel'
                    )}
                  </button>
                  <button 
                    type="button"
                    className="btn btn-outline back-button" 
                    onClick={() => navigate('/usuarios')}
                  >
                    Cancelar
                  </button>
                </>
              )}
            </div>
          </form>
        </div>
      </div>
      
      {/* Modal de resultados para creación masiva */}
      {showResultsModal && (
        <ResultsModal 
          results={massiveResults} 
          onClose={() => {
            setShowResultsModal(false);
            setMassiveResults(null);
          }} 
        />
      )}
    </div>
  );
}

export default CrearUsuario; 